import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  orderBy,
  limit
} from 'firebase/firestore';
import { calendarSchedulingService } from './calendarSchedulingService';

/**
 * V12 Business Diagnostic Service
 * 
 * "Your business firing on all twelve cylinders, like a Lamborghini engine."
 * 
 * Schedule:
 * - Day 1: Initial diagnostic
 * - Weeks 1-12: Weekly updates (at start of each week)
 * - Months 3-12: Monthly updates (at start of each month)
 * 
 * Firestore Schema:
 * v12_diagnostics/{userId}/history/{timestamp}
 * {
 *   completedAt: timestamp,
 *   weekNumber: number (1-12) or null for monthly,
 *   monthNumber: number (3-12) or null for weekly,
 *   cylinders: {
 *     finance: { items: [boolean, boolean, boolean], completed: number },
 *     team: { items: [boolean, boolean, boolean], completed: number },
 *     operations: { items: [boolean, boolean, boolean], completed: number },
 *     product: { items: [boolean, boolean, boolean], completed: number },
 *     customer: { items: [boolean, boolean, boolean], completed: number },
 *     growth: { items: [boolean, boolean, boolean], completed: number },
 *     marketing: { items: [boolean, boolean, boolean], completed: number },
 *     analytics: { items: [boolean, boolean, boolean], completed: number },
 *     ai: { items: [boolean, boolean, boolean], completed: number },
 *     it: { items: [boolean, boolean, boolean], completed: number },
 *     vendor: { items: [boolean, boolean, boolean], completed: number },
 *     legal: { items: [boolean, boolean, boolean], completed: number },
 *   },
 *   totalCompleted: number (0-36),
 *   cylindersComplete: number (0-12),
 * }
 * 
 * v12_diagnostics/{userId}/status
 * {
 *   lastCompletedAt: timestamp,
 *   currentWeek: number,
 *   currentMonth: number,
 *   nextDueAt: timestamp,
 *   totalDiagnosticsCompleted: number,
 *   latestScore: number,
 *   latestCylindersComplete: number,
 * }
 */

// V12 Cylinder Categories with their checklist items
export const V12_CYLINDERS = {
  finance: {
    id: 'finance',
    number: 1,
    title: 'Finance',
    emoji: '💰',
    items: [
      'Monthly income statements (P&L) completed',
      'Pro forma P&L updated monthly',
      'Forecasting and variance reviews conducted',
    ],
  },
  team: {
    id: 'team',
    number: 2,
    title: 'Team Composition & Performance',
    emoji: '👥',
    items: [
      'Compensation structure reviewed',
      'Incentives and performance metrics implemented',
      'Accountability and penalty systems clarified',
    ],
  },
  operations: {
    id: 'operations',
    number: 3,
    title: 'Operations Excellence',
    emoji: '⚙️',
    items: [
      'Core workflows evaluated and optimized',
      'Service delivery consistency validated',
      'Bottlenecks identified and resolved',
    ],
  },
  product: {
    id: 'product',
    number: 4,
    title: 'Product / Service Continuous Improvement',
    emoji: '🚀',
    items: [
      'Enhancements made based on performance data',
      'New features, capabilities, or processes rolled out',
      'Competitive benchmarking completed',
    ],
  },
  customer: {
    id: 'customer',
    number: 5,
    title: 'Customer Feedback Loop',
    emoji: '💬',
    items: [
      'Formal mechanism to collect feedback deployed',
      'Insights incorporated into service enhancements',
      'Customer satisfaction measured and tracked regularly',
    ],
  },
  growth: {
    id: 'growth',
    number: 6,
    title: 'Growth & Sales Engine',
    emoji: '📈',
    items: [
      'Lead generation and conversion processes documented',
      'Sales performance reviewed monthly',
      'Pipeline and revenue targets updated',
    ],
  },
  marketing: {
    id: 'marketing',
    number: 7,
    title: 'Marketing Differentiation',
    emoji: '🎯',
    items: [
      'Messaging and positioning refreshed',
      'Marketing channels reviewed for ROI',
      'Brand assets updated (visual, digital, collateral)',
    ],
  },
  analytics: {
    id: 'analytics',
    number: 8,
    title: 'Data Analytics & Optimization',
    emoji: '📊',
    items: [
      'KPI dashboards maintained (DAILY, WEEKLY)',
      'Deep-dive analytics performed (MONTHLY, QUARTERLY)',
      'Data used to drive operational and clinical decisions',
    ],
  },
  ai: {
    id: 'ai',
    number: 9,
    title: 'AI Integration',
    emoji: '🤖',
    items: [
      'AI tools or workflow automations implemented',
      'Staff trained on AI-supported processes',
      'AI impact measured (efficiency, accuracy, cost savings)',
    ],
  },
  it: {
    id: 'it',
    number: 10,
    title: 'IT Systems (External & Internal)',
    emoji: '💻',
    items: [
      'Internal systems audited for efficiency',
      'External technology evaluated for scalability',
      'Cybersecurity controls reviewed and updated',
    ],
  },
  vendor: {
    id: 'vendor',
    number: 11,
    title: 'Vendor Identification & Management',
    emoji: '🤝',
    items: [
      'Vendor list reviewed and optimized',
      'Performance and contract terms evaluated',
      'New vendors explored to reduce cost or increase capability',
    ],
  },
  legal: {
    id: 'legal',
    number: 12,
    title: 'Legal & Compliance',
    emoji: '⚖️',
    items: [
      'All contracts and agreements reviewed',
      'Licensing, certifications, and regulatory requirements audited',
      'Policies updated and communicated',
    ],
  },
};

export const v12DiagnosticService = {
  /**
   * Get the user's V12 diagnostic status
   */
  async getStatus(userId) {
    try {
      const statusRef = doc(db, 'v12_diagnostics', userId);
      const statusDoc = await getDoc(statusRef);
      
      if (statusDoc.exists()) {
        return { status: statusDoc.data(), error: null };
      }
      
      return { status: null, error: null };
    } catch (error) {
      console.error('[V12] Error getting status:', error);
      return { status: null, error: error.message };
    }
  },

  /**
   * Calculate when the next diagnostic is due based on enrollment date
   * 
   * Schedule:
   * - Day 1 (enrollment date): Initial diagnostic
   * - Weeks 1-12: At the start of each week (every 7 days from enrollment)
   * - Months 3-12: At the start of each month (monthly from week 12)
   */
  calculateNextDueDate(enrollmentDate, currentWeek, currentMonth) {
    const enrollment = enrollmentDate instanceof Date 
      ? enrollmentDate 
      : enrollmentDate?.toDate ? enrollmentDate.toDate() : new Date(enrollmentDate);
    
    const now = new Date();
    
    // First 12 weeks: weekly schedule
    if (currentWeek < 12) {
      const nextWeek = currentWeek + 1;
      const dueDate = new Date(enrollment);
      dueDate.setDate(dueDate.getDate() + (nextWeek * 7));
      return { dueDate, weekNumber: nextWeek, monthNumber: null };
    }
    
    // After week 12: monthly schedule for remaining 9 months (months 3-12)
    if (currentMonth < 12) {
      const nextMonth = Math.max(3, currentMonth + 1); // Start at month 3 after week 12
      const dueDate = new Date(enrollment);
      dueDate.setMonth(dueDate.getMonth() + nextMonth);
      return { dueDate, weekNumber: null, monthNumber: nextMonth };
    }
    
    // Course complete (12 months done)
    return { dueDate: null, weekNumber: null, monthNumber: 12 };
  },

  /**
   * Check if the user needs to complete a diagnostic
   */
  async checkIfDue(userId, enrollmentDate) {
    try {
      const { status } = await this.getStatus(userId);
      const enrollment = enrollmentDate instanceof Date 
        ? enrollmentDate 
        : enrollmentDate?.toDate ? enrollmentDate.toDate() : new Date(enrollmentDate);
      
      const now = new Date();
      const daysSinceEnrollment = Math.floor((now - enrollment) / (1000 * 60 * 60 * 24));
      
      // Day 1: Initial diagnostic
      if (!status || !status.lastCompletedAt) {
        return {
          isDue: true,
          isFirstTime: true,
          weekNumber: 0,
          monthNumber: null,
          daysUntilDue: 0,
        };
      }
      
      const lastCompleted = status.lastCompletedAt?.toDate ? status.lastCompletedAt.toDate() : new Date(status.lastCompletedAt);
      const currentWeek = status.currentWeek || 0;
      const currentMonth = status.currentMonth || 0;
      
      // Calculate which period we should be in based on days
      const weeksSinceEnrollment = Math.floor(daysSinceEnrollment / 7);
      
      // Still in weekly phase (first 12 weeks)
      if (weeksSinceEnrollment < 12) {
        if (currentWeek < weeksSinceEnrollment) {
          return {
            isDue: true,
            isFirstTime: false,
            weekNumber: weeksSinceEnrollment,
            monthNumber: null,
            daysUntilDue: 0,
          };
        }
        
        // Calculate days until next week starts
        const nextWeekStart = new Date(enrollment);
        nextWeekStart.setDate(nextWeekStart.getDate() + ((currentWeek + 1) * 7));
        const daysUntilDue = Math.ceil((nextWeekStart - now) / (1000 * 60 * 60 * 24));
        
        return {
          isDue: false,
          isFirstTime: false,
          weekNumber: currentWeek,
          monthNumber: null,
          daysUntilDue: Math.max(0, daysUntilDue),
          nextDueAt: nextWeekStart,
        };
      }
      
      // Monthly phase (months 3-12)
      const monthsSinceEnrollment = Math.floor(daysSinceEnrollment / 30);
      
      if (monthsSinceEnrollment >= 3 && currentMonth < Math.min(monthsSinceEnrollment, 12)) {
        return {
          isDue: true,
          isFirstTime: false,
          weekNumber: null,
          monthNumber: Math.min(monthsSinceEnrollment, 12),
          daysUntilDue: 0,
        };
      }
      
      // Calculate days until next month starts
      const nextMonthStart = new Date(enrollment);
      nextMonthStart.setMonth(nextMonthStart.getMonth() + currentMonth + 1);
      const daysUntilDue = Math.ceil((nextMonthStart - now) / (1000 * 60 * 60 * 24));
      
      return {
        isDue: false,
        isFirstTime: false,
        weekNumber: null,
        monthNumber: currentMonth,
        daysUntilDue: Math.max(0, daysUntilDue),
        nextDueAt: nextMonthStart,
      };
    } catch (error) {
      console.error('[V12] Error checking if due:', error);
      return { isDue: false, error: error.message };
    }
  },

  /**
   * Save a completed diagnostic
   */
  async saveDiagnostic(userId, cylinderData, weekNumber = null, monthNumber = null) {
    try {
      const timestamp = new Date().toISOString();
      
      // Calculate totals
      let totalCompleted = 0;
      let cylindersComplete = 0;
      
      const processedCylinders = {};
      
      Object.keys(V12_CYLINDERS).forEach(key => {
        const items = cylinderData[key] || [false, false, false];
        const completed = items.filter(Boolean).length;
        totalCompleted += completed;
        if (completed === 3) cylindersComplete++;
        
        processedCylinders[key] = {
          items,
          completed,
        };
      });
      
      // Save to history
      const historyRef = doc(db, 'v12_diagnostics', userId, 'history', timestamp);
      await setDoc(historyRef, {
        completedAt: new Date(),
        weekNumber,
        monthNumber,
        cylinders: processedCylinders,
        totalCompleted,
        cylindersComplete,
      });
      
      // Update status
      const statusRef = doc(db, 'v12_diagnostics', userId);
      const statusDoc = await getDoc(statusRef);
      const previousTotal = statusDoc.exists() ? (statusDoc.data().totalDiagnosticsCompleted || 0) : 0;
      
      await setDoc(statusRef, {
        lastCompletedAt: new Date(),
        currentWeek: weekNumber !== null ? weekNumber : (statusDoc.exists() ? statusDoc.data().currentWeek : 0),
        currentMonth: monthNumber !== null ? monthNumber : (statusDoc.exists() ? statusDoc.data().currentMonth : 0),
        totalDiagnosticsCompleted: previousTotal + 1,
        latestScore: totalCompleted,
        latestCylindersComplete: cylindersComplete,
        latestCylinderData: processedCylinders,
        updatedAt: new Date(),
      });
      
      // Mark calendar event as completed
      await calendarSchedulingService.markScheduledEventComplete(userId, 'v12Diagnostic', new Date());
      
      return { success: true, totalCompleted, cylindersComplete, error: null };
    } catch (error) {
      console.error('[V12] Error saving diagnostic:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get the latest diagnostic data for a user
   */
  async getLatestDiagnostic(userId) {
    try {
      const statusRef = doc(db, 'v12_diagnostics', userId);
      const statusDoc = await getDoc(statusRef);
      
      if (!statusDoc.exists()) {
        return { diagnostic: null, error: null };
      }
      
      return { diagnostic: statusDoc.data(), error: null };
    } catch (error) {
      console.error('[V12] Error getting latest diagnostic:', error);
      return { diagnostic: null, error: error.message };
    }
  },

  /**
   * Get diagnostic history for a user
   */
  async getDiagnosticHistory(userId, limitCount = 12) {
    try {
      const historyQuery = query(
        collection(db, 'v12_diagnostics', userId, 'history'),
        orderBy('completedAt', 'desc'),
        limit(limitCount)
      );
      
      const historySnapshot = await getDocs(historyQuery);
      const history = historySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      return { history, error: null };
    } catch (error) {
      console.error('[V12] Error getting history:', error);
      return { history: [], error: error.message };
    }
  },

  /**
   * Get V12 status for all users (admin use)
   */
  async getAllUsersV12Status() {
    try {
      const statusQuery = query(collection(db, 'v12_diagnostics'));
      const statusSnapshot = await getDocs(statusQuery);
      
      const statuses = {};
      statusSnapshot.docs.forEach(doc => {
        statuses[doc.id] = doc.data();
      });
      
      return { statuses, error: null };
    } catch (error) {
      console.error('[V12] Error getting all statuses:', error);
      return { statuses: {}, error: error.message };
    }
  },

  /**
   * Get progress summary string for a user
   */
  getProgressSummary(status) {
    if (!status) return 'Not started';
    
    const { currentWeek, currentMonth, latestCylindersComplete } = status;
    
    let phase = '';
    if (currentMonth >= 12) {
      phase = 'Course Complete';
    } else if (currentMonth >= 3) {
      phase = `Month ${currentMonth}/12`;
    } else if (currentWeek > 0) {
      phase = `Week ${currentWeek}/12`;
    } else {
      phase = 'Day 1';
    }
    
    return `${phase} • ${latestCylindersComplete || 0}/12 cylinders firing`;
  },

  /**
   * Force trigger diagnostic (for testing/admin)
   */
  async forceTriggerDiagnostic(userId) {
    try {
      const statusRef = doc(db, 'v12_diagnostics', userId);
      await updateDoc(statusRef, {
        lastCompletedAt: new Date(Date.now() - (8 * 24 * 60 * 60 * 1000)), // 8 days ago
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
};

export default v12DiagnosticService;

